import * as React from "react";
import clsx from "clsx";
import { Info, Plus, X } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import * as formInput from "../../../ui/components/Form/FormInput.style.css";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import { useI18n } from "../../lib/i18n";
import * as fieldStyles from "./OrganizationFormFields.css";
import * as editStyles from "./OrganizationEditPage.css";

const COUNTRY_OPTIONS = [
  { code: "1", label: "🇺🇸 +1" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "1", label: "🇨🇦 +1" },
] as const;

function formatPhoneDisplay(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

export interface OrganizationFormValues {
  name: string;
  description: string;
  logoUrl: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  emailPatterns: string[];
}

export type OrganizationFieldChanges = Partial<Record<keyof OrganizationFormValues, boolean>>;

export const emptyOrganizationFormValues = (): OrganizationFormValues => ({
  name: "",
  description: "",
  logoUrl: "",
  email: "",
  phoneCountryCode: "1",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  emailPatterns: [],
});

export interface OrganizationDetailsFieldsProps {
  values: OrganizationFormValues;
  onChange: (patch: Partial<OrganizationFormValues>) => void;
  changed?: OrganizationFieldChanges;
  /** Rendered in place of the logo trigger — callers pass a configured `<MediaUploader />`. */
  logoUploader?: React.ReactNode;
  showNameAvailability?: boolean;
  nameAvailable?: boolean | null;
  nameChecking?: boolean;
  onNameChange?: (value: string) => void;
}

export function OrganizationDetailsFields({
  values,
  onChange,
  changed = {},
  logoUploader,
  showNameAvailability,
  nameAvailable,
  nameChecking,
  onNameChange,
}: OrganizationDetailsFieldsProps) {
  const { t } = useI18n();
  return (
    <>
      <div style={{ position: "relative" }}>
        <TextInput
          label={t("pro.orgs.nameRequired")}
          placeholder="Acme Energy Co."
          value={values.name}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ name: v });
            onNameChange?.(v);
          }}
          error={nameAvailable === false ? "This name is already taken" : undefined}
          changed={changed.name}
        />
        {showNameAvailability && nameAvailable === true && (
          <>
            <span
              style={{
                position: "absolute",
                right: 12,
                top: 37,
                display: "flex",
                alignItems: "center",
                pointerEvents: "none",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#057A55" strokeWidth="2.5" aria-hidden>
                <circle cx="12" cy="12" r="10" stroke="#057A55" strokeWidth="1.5" fill="#DEF7EC" />
                <polyline points="8 12 11 15 16 9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span style={{ fontSize: 12, color: "#057A55", marginTop: 6, display: "block" }}>
              Name available
            </span>
          </>
        )}
        {showNameAvailability && nameChecking && (
          <span style={{ fontSize: 12, color: "#6B7280", marginTop: 6, display: "block" }}>
            Checking availability…
          </span>
        )}
      </div>

      <TextInput
        label={t("pro.orgs.description")}
        placeholder={t("pro.orgs.descriptionPlaceholder")}
        value={values.description}
        onChange={(e) => onChange({ description: e.target.value })}
        changed={changed.description}
      />

      {logoUploader && (
        <div>
          <Text level="body-small" weight="bold" style={{ marginBottom: 8 }}>
            Logo
          </Text>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {logoUploader}
          </div>
        </div>
      )}
    </>
  );
}

export interface OrganizationContactFieldsProps {
  values: OrganizationFormValues;
  onChange: (patch: Partial<OrganizationFormValues>) => void;
  changed?: OrganizationFieldChanges;
}

export function OrganizationContactFields({
  values,
  onChange,
  changed = {},
}: OrganizationContactFieldsProps) {
  const { t } = useI18n();
  const phoneChanged = Boolean(changed.phoneCountryCode || changed.phone);

  return (
    <>
      <TextInput
        label={t("pro.orgs.email")}
        placeholder="contact@example.com"
        value={values.email}
        onChange={(e) => onChange({ email: e.target.value })}
        changed={changed.email}
      />

      <div className={editStyles.phoneFieldGroup}>
        <span className={editStyles.phoneLabel}>{t("pro.orgs.phone")}</span>
        <div className={authText.phoneRow} style={{ alignItems: "flex-end" }}>
          <div className={authText.phoneCountryWrapper}>
            <select
              className={clsx(
                authText.countrySelect,
                phoneChanged && formInput.inputChanged,
              )}
              value={values.phoneCountryCode}
              onChange={(e) => onChange({ phoneCountryCode: e.target.value })}
              aria-label={t("pro.orgs.countryCode")}
            >
              {COUNTRY_OPTIONS.map((opt, i) => (
                <option key={`${opt.code}-${i}`} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              inputMode="tel"
              placeholder="(555) 000-0000"
              aria-label={t("pro.orgs.phoneNumber")}
              value={formatPhoneDisplay(values.phone)}
              onChange={(e) => onChange({ phone: stripPhoneFormatting(e.target.value) })}
              changed={phoneChanged}
            />
          </div>
        </div>
      </div>

      <TextInput
        label={t("pro.orgs.address")}
        placeholder="123 Main St"
        value={values.address}
        onChange={(e) => onChange({ address: e.target.value })}
        changed={changed.address}
      />

      <div className={editStyles.twoColRow}>
        <TextInput
          label={t("pro.orgs.city")}
          placeholder="Salt Lake City"
          value={values.city}
          onChange={(e) => onChange({ city: e.target.value })}
          changed={changed.city}
        />
        <TextInput
          label={t("pro.orgs.state")}
          placeholder="UT"
          value={values.state}
          onChange={(e) => onChange({ state: e.target.value })}
          changed={changed.state}
        />
      </div>

      <div className={editStyles.twoColRow}>
        <TextInput
          label={t("pro.orgs.zip")}
          placeholder="84101"
          value={values.zip}
          onChange={(e) => onChange({ zip: e.target.value })}
          changed={changed.zip}
        />
        <TextInput
          label={t("pro.orgs.country")}
          placeholder="US"
          value={values.country}
          onChange={(e) => onChange({ country: e.target.value })}
          changed={changed.country}
        />
      </div>
    </>
  );
}

const AUTO_APPROVED_PATTERNS_TOOLTIP =
  "Verified users matching a pattern can join instantly as members (e.g. @lionenergy.com).";

function validatePatternDraft(
  trimmed: string,
  patterns: string[],
): string | null {
  if (!trimmed) return "Enter a pattern.";
  if (trimmed.length > 255) return "Pattern is too long.";
  const valid =
    trimmed.includes("@") &&
    (trimmed.startsWith("@") || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed));
  if (!valid) return "Use @domain.com or a full email address.";
  if (patterns.includes(trimmed)) return "Pattern already added.";
  return null;
}

export function AutoApprovedPatternsLabel() {
  const { t } = useI18n();
  return (
    <div className={fieldStyles.patternLabelRow}>
      <Text level="body-small" weight="bold">
        Auto approved email patterns
      </Text>
      <span className={fieldStyles.infoTooltipWrapper}>
        <button
          type="button"
          className={fieldStyles.infoButton}
          aria-label={t("pro.orgs.aboutAutoApprovedPatterns")}
        >
          <Info size={14} aria-hidden />
        </button>
        <span className={fieldStyles.infoTooltip} role="tooltip">
          {AUTO_APPROVED_PATTERNS_TOOLTIP}
        </span>
      </span>
    </div>
  );
}

export interface AutoApprovedEmailsFieldProps {
  patterns: string[];
  onChange: (patterns: string[]) => void;
  changed?: boolean;
}

export function AutoApprovedEmailsField({
  patterns,
  onChange,
  changed,
}: AutoApprovedEmailsFieldProps) {
  const { t } = useI18n();
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [patternError, setPatternError] = React.useState<string | null>(null);

  const closeAddModal = React.useCallback(() => {
    setAddModalOpen(false);
    setDraft("");
    setPatternError(null);
  }, []);

  const openAddModal = () => {
    setDraft("");
    setPatternError(null);
    setAddModalOpen(true);
  };

  const addPattern = () => {
    const trimmed = draft.trim().toLowerCase();
    const error = validatePatternDraft(trimmed, patterns);
    if (error) {
      setPatternError(error);
      return;
    }
    onChange([...patterns, trimmed]);
    closeAddModal();
  };

  return (
    <div>
      <AutoApprovedPatternsLabel />

      {patterns.length === 0 ? (
        <div className={editStyles.centeredEmpty}>
          <p className={editStyles.centeredEmptyMessage}>
            {t("pro.orgs.noPatternsYet")}
          </p>
          <Button
            type="button"
            color="primary"
            variant="solid"
            iconLeft={<Plus size={16} />}
            onClick={openAddModal}
          >
            Add Pattern
          </Button>
        </div>
      ) : (
        <>
          <div
            className={clsx(
              fieldStyles.patternListArea,
              changed && fieldStyles.patternChipListChanged,
            )}
          >
            <div className={fieldStyles.patternChipList}>
              {patterns.map((pattern) => (
                <span key={pattern} className={fieldStyles.patternChip}>
                  {pattern}
                  <button
                    type="button"
                    onClick={() => onChange(patterns.filter((p) => p !== pattern))}
                    aria-label={`Remove ${pattern}`}
                    className={fieldStyles.patternChipRemove}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <Button
            type="button"
            color="neutral"
            variant="outline"
            iconLeft={<Plus size={14} />}
            onClick={openAddModal}
          >
            Add Pattern
          </Button>
        </>
      )}

      <Modal
        open={addModalOpen}
        setOpen={(open) => {
          setAddModalOpen(open);
          if (!open) {
            setDraft("");
            setPatternError(null);
          }
        }}
        minWidth="400px"
        maxWidth="400px"
        minHeight="auto"
      >
        <div className={fieldStyles.addPatternModal}>
          <Text level="h3" weight="bold">
            Add pattern
          </Text>
          <TextInput
            label={t("pro.orgs.emailPattern")}
            placeholder="@lionenergy.com"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setPatternError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPattern();
              }
            }}
            error={patternError ?? undefined}
            autoFocus
          />
          <div className={fieldStyles.addPatternModalActions}>
            <Button type="button" color="neutral" variant="ghost" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button type="button" color="primary" variant="solid" onClick={addPattern}>
              Add pattern
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export interface OrganizationFormFieldsProps {
  values: OrganizationFormValues;
  onChange: (patch: Partial<OrganizationFormValues>) => void;
  /** Rendered in place of the logo trigger — callers pass a configured `<MediaUploader />`. */
  logoUploader?: React.ReactNode;
  showNameAvailability?: boolean;
  nameAvailable?: boolean | null;
  nameChecking?: boolean;
  onNameChange?: (value: string) => void;
}

export function OrganizationFormFields({
  values,
  onChange,
  logoUploader,
  showNameAvailability,
  nameAvailable,
  nameChecking,
  onNameChange,
}: OrganizationFormFieldsProps) {
  return (
    <>
      <OrganizationDetailsFields
        values={values}
        onChange={onChange}
        logoUploader={logoUploader}
        showNameAvailability={showNameAvailability}
        nameAvailable={nameAvailable}
        nameChecking={nameChecking}
        onNameChange={onNameChange}
      />
      <OrganizationContactFields values={values} onChange={onChange} />
      <AutoApprovedEmailsField
        patterns={values.emailPatterns}
        onChange={(emailPatterns) => onChange({ emailPatterns })}
      />
    </>
  );
}

export function organizationFormValuesToPayload(
  values: OrganizationFormValues,
  options?: { slug?: string },
): {
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  emailPatterns?: string[];
} {
  const trimmedName = values.name.trim();
  const slug =
    options?.slug ??
    trimmedName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 200);

  return {
    name: trimmedName,
    slug,
    description: values.description.trim() || undefined,
    logoUrl: values.logoUrl.trim() || undefined,
    email: values.email.trim() || undefined,
    phone: values.phone.trim() ? `+${values.phoneCountryCode}${values.phone.trim()}` : undefined,
    address: values.address.trim() || undefined,
    city: values.city.trim() || undefined,
    state: values.state.trim() || undefined,
    zip: values.zip.trim() || undefined,
    country: values.country.trim() || undefined,
    emailPatterns: values.emailPatterns.length > 0 ? values.emailPatterns : undefined,
  };
}

export function parsePhoneFromStored(phone: string | null | undefined): {
  phoneCountryCode: string;
  phone: string;
} {
  if (!phone?.trim()) return { phoneCountryCode: "1", phone: "" };
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("1") && digits.length === 11) {
    return { phoneCountryCode: "1", phone: digits.slice(1) };
  }
  if (digits.startsWith("44") && digits.length > 10) {
    return { phoneCountryCode: "44", phone: digits.slice(2) };
  }
  return { phoneCountryCode: "1", phone: digits.slice(-10) };
}

export function organizationToFormValues(org: {
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  emailPatterns?: string[];
  slug?: string;
}): OrganizationFormValues {
  const { phoneCountryCode, phone } = parsePhoneFromStored(org.phone);
  return {
    name: org.name,
    description: org.description ?? "",
    logoUrl: org.logoUrl ?? "",
    email: org.email ?? "",
    phoneCountryCode,
    phone,
    address: org.address ?? "",
    city: org.city ?? "",
    state: org.state ?? "",
    zip: org.zip ?? "",
    country: org.country ?? "",
    emailPatterns: org.emailPatterns ?? [],
  };
}
